import { useCallback, useEffect, useRef } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { config } from '_config';
import { useHandleApiErrors } from './useHandleApiErrors';

const MAX_RETRIES = 2;

export const BT_INSTITUTIONS_GRAPHQL = gql`
  query balanceTransferInstitutions($region: Region!) {
    balanceTransferInstitutions(region: $region) {
      institutions {
        id
        name
      }
    }
  }
`;

function useQueryBalanceTransferInstitutions({ onSuccess, onError, fallbackValue }) {
  const { handleError } = useHandleApiErrors('hook:useQueryBalanceTransferInstitutions', onError);
  const retryCountRef = useRef(0);

  const [query, { loading, error, data }] = useLazyQuery(BT_INSTITUTIONS_GRAPHQL, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (error) {
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        query({ variables: { region: config.countryCode } });
        return;
      }

      if (fallbackValue) {
        onSuccess(fallbackValue);
        return;
      }
      handleError(error);
    }
  }, [error, handleError, fallbackValue, onSuccess, query]);

  useEffect(() => {
    if (data) {
      retryCountRef.current = 0;
      onSuccess(data.balanceTransferInstitutions?.institutions ?? []);
    }
  }, [data, onSuccess]);

  const queryBTInstitutions = useCallback(() => {
    retryCountRef.current = 0;
    const input = {
      region: config.countryCode,
    };
    query({ variables: input });
  }, [query]);

  return {
    queryBTInstitutions,
    loading,
  };
}

export { useQueryBalanceTransferInstitutions };
